import axios, { AxiosError } from "axios";
import ValidationError, { ValidationErrorResponse } from "./ValidationError";
import ServerError from "./ServerError";
import AuthError from "./AuthError";
import Cookies from "js-cookie";
import NotFoundError from "./NotFoundError";
import { RespuestaBlob } from "../utils/blobUtils";

export const JWT_DIVERTIDOGS_COOKIE: string = "HNrG8!nX_^67~xJ?";

export const generarRuta = (endpoint: string) => {
  return `${process.env.REACT_APP_DIVERTIDOGS_API_URL}${endpoint}`;
};

interface ServerErrorBody {
  mensaje?: string;
  erroresValidacion?: ValidationErrorResponse;
}

export const handleError = (error: any) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (axiosError.response) {
      const errorResponse = axiosError.response.data as ServerErrorBody;
      if (errorResponse) {
        if (errorResponse.erroresValidacion) {
          const erroresValidacion =
            errorResponse.erroresValidacion as ValidationErrorResponse;
          if (Object.keys(erroresValidacion).length !== 0) {
            throw new ValidationError(erroresValidacion);
          }
        }
        if (errorResponse.mensaje) {
          if (axiosError.response.status === 401) {
            throw new AuthError(errorResponse.mensaje);
          }
          if (axiosError.response.status === 404) {
            throw new NotFoundError(errorResponse.mensaje);
          }
          throw new ServerError(errorResponse.mensaje);
        }
      }
      if (axiosError.response.status === 401)
        throw new AuthError(axiosError.message);
      throw new ServerError(axiosError.message);
    } else {
      throw new ServerError(axiosError.message);
    }
  } else {
    const err = error as Error;
    throw new ServerError(err.message);
  }
};

export const get = async <BodyType>(
  endpointUrl: string,
  withAuth: boolean = true
): Promise<BodyType | undefined> => {
  try {
    let response = null;

    if (withAuth) {
      response = await axios.get(generarRuta(endpointUrl), {
        headers: { Authorization: generarAuthHeader() },
      });
    } else {
      response = await axios.get(generarRuta(endpointUrl));
    }

    if (response.data !== undefined && response.data !== null) {
      return Promise.resolve(response.data as BodyType);
    } else {
      return Promise.resolve(undefined);
    }
  } catch (error) {
    handleError(error);
  }
};

const extraerNombreArchivoDeHeader = (contentDisposition: string | undefined) => {
  if (!contentDisposition) {
    return undefined;
  }

  const nombreArchivoMatch =
    contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
    contentDisposition.match(/filename="?([^";]+)"?/i);

  if (!nombreArchivoMatch || !nombreArchivoMatch[1]) {
    return undefined;
  }

  return decodeURIComponent(nombreArchivoMatch[1]);
};

const extraerNombreYExtension = (nombreArchivo: string | undefined) => {
  if (!nombreArchivo) {
    return { nombre: undefined, extension: undefined };
  }

  const ultimoPunto = nombreArchivo.lastIndexOf(".");
  if (ultimoPunto <= 0 || ultimoPunto === nombreArchivo.length - 1) {
    return { nombre: nombreArchivo, extension: undefined };
  }

  return {
    nombre: nombreArchivo.substring(0, ultimoPunto),
    extension: nombreArchivo.substring(ultimoPunto + 1),
  };
};

export const getBlob = async (
  endpointUrl: string,
  withAuth: boolean = true
): Promise<RespuestaBlob> => {
  try {
    let response = null;

    if (withAuth) {
      response = await axios.get<Blob>(generarRuta(endpointUrl), {
        headers: { Authorization: generarAuthHeader() },
        responseType: "blob",
      });
    } else {
      response = await axios.get<Blob>(generarRuta(endpointUrl), {
        responseType: "blob",
      });
    }

    const nombreArchivo = extraerNombreArchivoDeHeader(
      response.headers["content-disposition"]
    );
    const { nombre, extension } = extraerNombreYExtension(nombreArchivo);

    return Promise.resolve({
      contenido: response.data,
      nombre,
      extension,
      fecha: new Date().toISOString(),
    });
  } catch (error) {
    handleError(error);
  }

  throw new Error("Unhandled flow GET blob");
};

export const generarAuthHeader = () => {
  const token = Cookies.get(JWT_DIVERTIDOGS_COOKIE);
  return `Bearer ${token}`;
};

export const post = async <RequestBodyType = void, ResponseBodyType = void>(
  endpointUrl: string,
  body: RequestBodyType | undefined = undefined,
  withAuth: boolean = true
): Promise<ResponseBodyType> => {
  try {
    let response = null;

    if (withAuth) {
      const token = Cookies.get(JWT_DIVERTIDOGS_COOKIE);
      response = await axios.post(generarRuta(endpointUrl), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      response = await axios.post(generarRuta(endpointUrl), body);
    }

    if (response.data && response.status === 200) {
      return Promise.resolve(response.data as ResponseBodyType);
    }
    if (response.status === 200 && !response.data) {
      return Promise.resolve({} as ResponseBodyType);
    } else {
      throw new Error("No data recieved from POST request");
    }
  } catch (error) {
    handleError(error);
  }
  throw new Error("Unhandled flow POST");
};

export const patch = async <RequestBodyType, ResponseBodyType = void>(
  endpointUrl: string,
  body: RequestBodyType,
  withAuth: boolean = true
): Promise<ResponseBodyType> => {
  try {
    let response = null;

    if (withAuth) {
      const token = Cookies.get(JWT_DIVERTIDOGS_COOKIE);
      response = await axios.patch(generarRuta(endpointUrl), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      response = await axios.patch(generarRuta(endpointUrl), body);
    }

    if (response.status === 200) {
      return Promise.resolve(response.data as ResponseBodyType);
    } else {
      throw new Error("No data recieved from PATCH request");
    }
  } catch (error) {
    handleError(error);
  }
  throw new Error("Unhandled flow POST");
};

export const put = async <RequestBodyType, ResponseBodyType = void>(
  endpointUrl: string,
  body: RequestBodyType,
  withAuth: boolean = true
): Promise<ResponseBodyType | undefined> => {
  try {
    let response = null;

    if (withAuth) {
      const token = Cookies.get(JWT_DIVERTIDOGS_COOKIE);
      response = await axios.put(generarRuta(endpointUrl), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      response = await axios.put(generarRuta(endpointUrl), body);
    }

    if (response.status === 200) {
      return Promise.resolve(response.data as ResponseBodyType);
    } else {
      return Promise.resolve(undefined);
    }
  } catch (error) {
    handleError(error);
  }
  throw new Error("Unhandled flow PUT");
};

export const apiDelete = async (
  endpointUrl: string,
  withAuth: boolean = true
): Promise<void> => {
  try {
    let response = null;

    if (withAuth) {
      const token = Cookies.get(JWT_DIVERTIDOGS_COOKIE);
      response = await axios.delete(generarRuta(endpointUrl), {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      response = await axios.delete(generarRuta(endpointUrl));
    }

    if (response.status === 200) {
      return Promise.resolve();
    } else {
      throw Error("DELETE API error ");
    }
  } catch (error) {
    handleError(error);
  }
  throw new Error("Unhandled flow DELETE");
};
